import { useEffect, useRef } from "react";
import {
	WdprNavMenuButton,
	WdprNavLoginButton,
	WdprNotificationIndicator,
} from "components/vista";
import { defaultIconVariant, isProfileSection, getAvatarSrc } from "config-txp";
import { useGenericContext } from "@wdpr/groot-components";
import AnalyticsWrapper from "components/shared/AnalyticsWrapper";
import { buildHref } from "utils/link";
import { getCdn } from "utils";

/**
 * After the web-component renders its shadow DOM, reach into the
 * chevron <button> and patch its ARIA attributes.
 *
 * Strategy: no aria-hidden. In browse mode the chevron keeps a generic
 * "Toggle menu" label (avoids duplicating the adjacent link's title).
 * On focus (Tab) the label switches to the section title so screen
 * readers announce e.g. "Parks, button, collapsed".
 */
const useChevronAria = (ref, isExpanded, controlsId, title) => {
	useEffect(() => {
		const el = ref.current;
		if (!el?.shadowRoot) return;
		const chevron = el.shadowRoot.querySelector('button[aria-label]');
		if (!chevron) return;

		// Always set expanded and controls
		chevron.setAttribute("aria-expanded", String(isExpanded));
		if (controlsId) chevron.setAttribute("aria-controls", controlsId);

		// Only set aria-label on focus (avoids duplication with link in browse mode)
		const handleFocus = () => {
			if (title) chevron.setAttribute("aria-label", title);
		};
		const handleBlur = () => {
			chevron.setAttribute("aria-label", "Toggle menu");
		};

		chevron.addEventListener("focus", handleFocus);
		chevron.addEventListener("blur", handleBlur);

		return () => {
			chevron.removeEventListener("focus", handleFocus);
			chevron.removeEventListener("blur", handleBlur);
		};
	}, [ref, isExpanded, controlsId, title]);
};

export const SectionTitleTxp = ({
	title,
	id,
	hoveredSection,
	handleTxpSectionButtonPressed,
	variant,
	link,
	defaultAvatar = "",
}) => {
	const {
		profileData: { user = null, isLoading },
		config: { brand, store, basePath, showSections },
		showUserStatus,
	} = useGenericContext();
	const showLoggedInUser = showUserStatus && !isLoading && !!user;
	const inverse = variant === defaultIconVariant;
	const showNotificationBadge = showUserStatus && user?.messages?.itemCount > 0;

	const menuButtonRef = useRef(null);
	const loginButtonRef = useRef(null);

	const isOpen = id === hoveredSection;
	const flyoutId = id ? `syndicated-flyout-${id}` : undefined;

	useChevronAria(menuButtonRef, isOpen, flyoutId, title);
	useChevronAria(loginButtonRef, isOpen, flyoutId, title);

	const processHref = (href) => buildHref(href, store, basePath, false);

	const processedHref = link?.href ? processHref(link.href) : "#";

	return isProfileSection(id) ? (
		!showSections && !showUserStatus ? null : (
			<AnalyticsWrapper analyticsLinkId={link?.analyticsLinkId}>
				<WdprNavLoginButton
					ref={loginButtonRef}
					a11yLabel={title}
					iconName="profile-about-me"
					active={isOpen}
					controlsId={flyoutId}
					inverse={inverse}
					loggedIn={showLoggedInUser}
					avatarSrc={
						showLoggedInUser
							? getAvatarSrc({ user, defaultAvatar, brand, getCdn })
							: undefined
					}
					allowKeyboardChevron={true}
					onWdprNavLoginPressed={handleTxpSectionButtonPressed}
					href={processedHref}
					target={link?.target}
				>
					<span aria-hidden="true">{title}</span>
					{showNotificationBadge ? (
						<WdprNotificationIndicator
							slot="notification"
							type="alert"
							number={user?.messages?.itemCount}
						/>
					) : null}
				</WdprNavLoginButton>
			</AnalyticsWrapper>
		)
	) : (
		<AnalyticsWrapper analyticsLinkId={link?.analyticsLinkId}>
			<WdprNavMenuButton
				ref={menuButtonRef}
				label={title}
				active={isOpen}
				_isOpen={isOpen}
				controlsId={flyoutId}
				onWdprNavMenuButtonPressed={handleTxpSectionButtonPressed}
				allowKeyboardChevron={true}
				inverse={inverse}
				href={processedHref}
				target={link?.target}
			/>
		</AnalyticsWrapper>
	);
};
